import 'maplibre-gl/dist/maplibre-gl.css';
import maplibregl, { type Map as MaplibreMap, type Marker as MaplibreMarker } from 'maplibre-gl';
import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  AiOutputFormat,
  AnalysisDisplayRules,
  DistrictMapPmtiles,
  FieldDef,
} from '@citizen-walk/shared-types';
import type { AiCategoryMastersResult, PostMinimal } from '../posts';
import { useEcwIdeas, useEcwInsights } from '../insights';
import type { IdeaMinimal, InsightMinimal } from '../insights';
import { AxisFilterPanel, useAxisFilter } from './analysisFilter';
// docs/107 オフライン残: glyphs + 地名 symbol 付き pmtiles スタイル / cache lookup / 自動切替。
import {
  addPmtilesSource,
  buildPmtilesStyle,
  lookupValidCache,
  type OfflineMapEntry,
} from '../map';
import { useOnlineStatus } from '../shell';
import type { AnalysisPerspective } from './analysisPerspectives';

/**
 * 分析アプリ「ヒートマップ」タブ (docs/107 Wave 7、old2 `components/ecw/HeatmapView.tsx` 591 行を正に parity)。
 *
 * old2 踏襲 (Wave 7):
 *   - **関係密度重み (postCounts 3 段式)**: 各投稿に紐づく気づき/処方箋の件数を数え、
 *     0 件→weightZero (シャドウ) / 1 件→weightOne (中間調) / 2 件以上→weightMultipleBase + count*1.5
 *     (old2 L226-230) を heatmap-weight に流す。
 *   - **targetType 切替 (気づき / 処方箋)**: insight の selectedPostIds、または idea→insightIds→
 *     insight.selectedPostIds の二段解決で件数を集計 (old2 L132-159)。
 *   - **象限色マーカー (getQuadrantColor)**: count >= 1 の投稿に Happy×Health 象限色の ● マーカー、
 *     サイズ = markerSizeBase + |Happy|*2、count>=2 は border 3px 強調 (old2 L258-301)。
 *   - **日付フィルタ**: 開始 / 終了 date で post を絞り込む (old2 L111-130、v2 は createdAt: Timestamp を
 *     YYYY-MM-DD 化して比較)。
 *   - **重み 3 段スライダー / マーカーサイズ / radius・blur・intensity・opacity スライダー** + 折りたたみ
 *     右上コントロールパネル (old2 L325-586)。
 *   - **色グラデーション 8 stop** (old2 L191-201、青→水→緑→黄→橙→赤の 8 stop)。
 *
 * 判断補正 (docs/107 §判断補正):
 *   - **データモデル**: HappinessScore / HealthScore は型付きフィールドでなく `post.extraFields[key]`
 *     から読む (v2 思想維持。shibuya seed は rating-scale postField で保持)。
 *   - **色 / テーマ**: コントロールパネルは v2 semantic token (slate/primary) で構成するが、ヒートマップ
 *     gradient と象限色 (getQuadrantColor) は old2 の意味色をそのまま保持する。
 *   - **v2 良差分維持**: M5 heatmapRules 由来 default (radius/intensity/opacity/weightMode/weightField/
 *     weightAgg/weightFormula/secondaryWeightField) と weightMode='score' (件数密度↔スコア重み) の切替を
 *     残す。weightMode='count' のとき old2 の postCounts 3 段重みを適用する。
 *
 * Wave 2 keep-alive 維持:
 *   - TabPanel が display 切替で keep-alive するため、ResizeObserver + 'analysisTabActivated' で
 *     再表示時に map.resize() する (MapViewImpl と同 pattern)。
 *
 * 制約:
 *   - jsdom で maplibre-gl が動かない (WebGL/Canvas 未対応) → maplibre は vi.mock で stub し、
 *     control UI / weight 計算のみ検証。実描画は E2E で別途。
 *   - 旧 usePmtilesMap オフライン PMTiles 切替は scope 外 (docs/107 オフライン残)。
 */

export interface AnalysisHeatmapViewProps {
  /** docs/29 §8-1: 追加の AI 解析の観点 (軸の候補に観点ごとの AI 解析の軸を足す)。 */
  perspectives?: readonly AnalysisPerspective[];
  /** 既定の観点の表示名 (追加の観点があるとき軸のラベルに付ける)。 */
  defaultPerspectiveLabel?: string | null;
  /** insight / idea subscribe 用 district id。 */
  districtId: string;
  posts: PostMinimal[];
  /**
   * マーカー (●) を選んだときの投稿詳細表示 (2026-09-07)。
   * 地図表示と同じ導線にするため追加。未指定ならマーカーはクリックできない。
   */
  onSelectPost?: (post: PostMinimal) => void;
  /** 初期 center (未指定は東京)。AnalysisPage が district.postAreaCenter を渡す。 */
  centerOverride?: { lat: number; lng: number };
  zoomOverride?: number;
  /** docs/107 Wave7 / Sprint AV: M5 heatmap rules (未指定なら old2 default 動作)。 */
  heatmapRules?: AnalysisDisplayRules['heatmap'];
  /* === 軸駆動フィルタ + 動的マーカー色 (マトリクス/分析画面と同じ matrixAxisModel エンジン) === */
  /** master 由来軸 (感情 / 対象 / 任意 templateVar)。 */
  resolver?: AiCategoryMastersResult;
  /** dataSettings.postFields 由来軸。 */
  postFields?: FieldDef[];
  /** M5 マトリクスルール (custom 軸 / hiddenAxisKeys / includeEmpty)。 */
  matrixRules?: AnalysisDisplayRules['matrix'];
  /** M3 出力フォーマット (format 駆動の軸 code 解決)。 */
  outputFormat?: AiOutputFormat | null;
  /**
   * docs/107 オフライン残: 地区の PMTiles メタデータ。
   * 指定 + キャッシュ済 + offline (navigator.onLine=false) のとき、OSM ラスターでなく
   * pmtiles (glyphs + 地名 symbol 付き) ベースを自動採用する。未指定なら従来の OSM 固定。
   */
  pmtilesMeta?: DistrictMapPmtiles | null;
}

interface OverlayMarker {
  id: string;
  lng: number;
  lat: number;
  /** 色分け軸 (master/postField/M5 由来) で解決したマーカー色。軸未選択は単色。 */
  color: string;
  /** マーカー直径 px (markerSizeBase + 関連件数で拡縮)。 */
  size: number;
  /** 関連件数 2 以上 = border 太枠強調。 */
  emphasized: boolean;
  title: string;
  /** 関連件数 (tooltip 用)。 */
  count: number;
}

/** weight 計算モード。'count' = old2 の postCounts 3 段式、'score' = extraFields スコア連動 (v2 拡張)。 */
type WeightMode = 'count' | 'score';

/** Sprint AV: M5 heatmap.weightAgg の許容値 (shared-types 由来、明示的に extract)。 */
type WeightAgg = NonNullable<NonNullable<AnalysisDisplayRules['heatmap']>['weightAgg']>;

const HEATMAP_SOURCE_ID = 'heatmap-points';
const HEATMAP_LAYER_ID = 'heatmap-layer';

const DEFAULT_CENTER = { lat: 35.6, lng: 139.7 }; // 東京 (M5 mapRules.defaultCenter 化は後続)
const DEFAULT_ZOOM = 13;

// docs/107 Wave7 / old2 HeatmapView.tsx L55-68 EcwHeatmapState 既定値を踏襲。
const DEFAULT_RADIUS = 30;
const DEFAULT_BLUR = 20;
const DEFAULT_INTENSITY = 1;
const DEFAULT_OPACITY = 0.8;
const DEFAULT_MARKER_SIZE = 12;
const DEFAULT_WEIGHT_ZERO = 0.01;
const DEFAULT_WEIGHT_ONE = 0.1;
const DEFAULT_WEIGHT_MULTIPLE_BASE = 3.0;

const OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// マーカー色は「色分け軸」(master/postField/M5 由来) で動的に塗る。軸未選択 / 値なしはこの単色。
// 旧 getQuadrantColor (Happy×Health 固定象限色) は廃止 (ECW 専用ハードコードのため)。
const DEFAULT_MARKER_COLOR = '#0EA5E9';

function readNumber(extraFields: Record<string, unknown> | null, key: string): number | null {
  if (!extraFields) return null;
  const v = extraFields[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Task D 動的化: 簡易 weightFormula evaluator。
 *
 * 受理する式の形は **2 オペランド + 演算子 1 つ** のみ:
 *   "A + B" / "A - B" / "A * B" / "A / B"
 *   オペランド = extraFields の key (string) または数値リテラル
 *
 * セキュリティ方針: `new Function` / `eval` は **使わない** (XSS / 任意コード実行リスク)。
 * 想定外の式 (3 項以上、括弧、関数呼び出し、空白なし演算子等) は null を返し fallback に委ねる。
 *
 * 戻り値: null = 評価不能 (式の構文 / 値解決失敗)、それ以外は評価結果 number。
 */
export function evaluateWeightFormula(
  formula: string,
  extraFields: Record<string, unknown> | null,
): number | null {
  if (!formula || typeof formula !== 'string') return null;
  // `A op B` 形式に分解 (空白で囲まれた演算子のみ受理)
  const m = formula.trim().match(/^(.+?)\s+([+\-*/])\s+(.+)$/);
  if (!m) return null;
  const [, leftRaw, op, rightRaw] = m;
  if (!leftRaw || !op || !rightRaw) return null;
  const resolve = (token: string): number | null => {
    const t = token.trim();
    // 数値リテラル
    if (/^-?\d+(\.\d+)?$/.test(t)) {
      const n = Number(t);
      return Number.isFinite(n) ? n : null;
    }
    // identifier (key 名)
    if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(t)) {
      return readNumber(extraFields, t);
    }
    return null;
  };
  const left = resolve(leftRaw);
  const right = resolve(rightRaw);
  if (left === null || right === null) return null;
  switch (op) {
    case '+':
      return left + right;
    case '-':
      return left - right;
    case '*':
      return left * right;
    case '/':
      return right === 0 ? null : left / right;
    default:
      return null;
  }
}

/**
 * docs/107 Wave7 / old2 HeatmapView.tsx L114-121 normalizeDate を踏襲。
 * 任意の日付文字列を YYYY-MM-DD に正規化 (date input の value と辞書順比較可能にする)。
 */
function normalizeDate(dateStr: string): string {
  if (!dateStr) return '';
  const pureDate = dateStr.split(/[ T]/)[0]?.replace(/\//g, '-') ?? '';
  const parts = pureDate.split('-');
  if (parts.length === 3) {
    if (parts[0]?.length === 4)
      return `${parts[0]}-${parts[1]?.padStart(2, '0')}-${parts[2]?.padStart(2, '0')}`;
    if (parts[2]?.length === 4)
      return `${parts[2]}-${parts[0]?.padStart(2, '0')}-${parts[1]?.padStart(2, '0')}`;
  }
  return pureDate;
}

/** post.createdAt (Timestamp) を YYYY-MM-DD に変換 (v2 は date 文字列でなく Timestamp 保持)。 */
function postDateString(post: PostMinimal): string {
  const ts = post.createdAt;
  if (!ts) return '';
  try {
    const d = ts.toDate();
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${y}-${mo}-${da}`;
  } catch {
    return '';
  }
}

/**
 * docs/107 Wave7 / old2 HeatmapView.tsx L132-159 postCounts を踏襲。
 * targetType 別に、各投稿に紐づく気づき (insight) / 処方箋 (idea) の件数を集計する。
 *   - insight: insight.selectedPostIds を直接カウント
 *   - idea: idea.insightIds → insight.selectedPostIds の二段解決でカウント
 */
function computePostCounts(
  posts: PostMinimal[],
  targetType: 'insight' | 'idea',
  insights: InsightMinimal[],
  ideas: IdeaMinimal[],
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const p of posts) counts[p.id] = 0;

  if (targetType === 'insight') {
    for (const ins of insights) {
      for (const pid of ins.selectedPostIds) {
        if (counts[pid] !== undefined) counts[pid]++;
      }
    }
  } else {
    const insightMap = new Map(insights.map((i) => [i.id, i]));
    for (const idea of ideas) {
      for (const insId of idea.insightIds) {
        const related = insightMap.get(insId);
        if (!related) continue;
        for (const pid of related.selectedPostIds) {
          if (counts[pid] !== undefined) counts[pid]++;
        }
      }
    }
  }
  return counts;
}

/**
 * docs/107 Wave7: 1 post の heatmap-weight 値を算出。
 *
 * - mode='count' (old2 既定): 関係密度 3 段重み
 *     0 件 → weightZero (シャドウ) / 1 件 → weightOne (中間調) / 2 件以上 → weightMultipleBase + count*1.5
 *   (old2 L226-230)
 * - mode='score' (v2 拡張): extraFields 由来スコア (M5 weightField/weightAgg/weightFormula)。
 *     - weightFormula 指定 → evaluator 優先
 *     - weightField 未指定 → |HappinessScore × HealthScore| / 9 (0..1 正規化)
 *     - weightField + product/abs-product → weightField × secondaryWeightField(既定 HealthScore)
 *     - それ以外 → weightField の値そのまま
 *
 * 戻り値: weight 値。score モードで未入力なら 0 (実質非表示)。
 */
function computeWeight(
  post: PostMinimal,
  count: number,
  mode: WeightMode,
  weightZero: number,
  weightOne: number,
  weightMultipleBase: number,
  weightField: string | undefined,
  weightAgg: WeightAgg | undefined,
  weightFormula: string | undefined,
  secondaryWeightField: string | undefined,
): number {
  if (mode === 'count') {
    if (count >= 2) return weightMultipleBase + count * 1.5;
    if (count === 1) return weightOne;
    return weightZero;
  }
  // mode === 'score' (v2 拡張): extraFields スコア連動
  const ef = post.extraFields;
  if (weightFormula && weightFormula.trim() !== '') {
    return evaluateWeightFormula(weightFormula, ef) ?? 0;
  }
  if (!weightField) {
    const happy = readNumber(ef, 'HappinessScore');
    const health = readNumber(ef, 'HealthScore');
    if (happy === null || health === null) return 0;
    return Math.abs(happy * health) / 9;
  }
  const primary = readNumber(ef, weightField);
  if (primary === null) return 0;
  if (weightAgg === 'product' || weightAgg === 'abs-product') {
    const secondaryFieldName =
      secondaryWeightField && secondaryWeightField.trim() !== ''
        ? secondaryWeightField
        : 'HealthScore';
    const secondary = readNumber(ef, secondaryFieldName);
    if (secondary === null) return 0;
    const product = primary * secondary;
    return weightAgg === 'abs-product' ? Math.abs(product) : product;
  }
  return primary;
}

export function AnalysisHeatmapView({
  perspectives,
  defaultPerspectiveLabel,
  districtId,
  posts,
  onSelectPost,
  centerOverride,
  zoomOverride,
  heatmapRules,
  pmtilesMeta,
  resolver,
  postFields,
  matrixRules,
  outputFormat,
}: AnalysisHeatmapViewProps) {
  // 軸駆動フィルタ (master/dataSettings/M5 由来、マトリクス/分析画面と同じ)。
  const axisFilter = useAxisFilter(posts, {
    resolver,
    postFields,
    matrixRules,
    outputFormat,
    perspectives,
    defaultPerspectiveLabel,
  });
  const [showFilters, setShowFilters] = useState(false);
  // マーカーの「色分け軸」。空 = 単色 (mode 既定色)。既定は最初の軸候補 (あれば)。
  const [colorAxisKey, setColorAxisKey] = useState('');
  const container = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const mapLoadedRef = useRef(false);
  // 地図の生成世代 (2026-09-07)。地図は タイル種別の解決を待って作られるため、初回描画時点では
  // まだ存在しない。マーカーや source 同期の effect が「地図が無いまま 1 度走って終わり」に
  // ならないよう、生成のたびにこの値を進めて依存に入れる。
  // (これが無いと、投稿が既に手元にある状態でタブを開いたときにマーカーが出ないことがあった。)
  const [mapGeneration, setMapGeneration] = useState(0);
  const quadrantMarkersRef = useRef<MaplibreMarker[]>([]);
  // マーカー click → 投稿詳細。マーカーは MapLibre が DOM に挿すため、最新の handler と
  // 投稿一覧を ref 越しに読む (依存に入れるとマーカーを毎回作り直すことになる)。
  const selectHandlerRef = useRef<((postId: string) => void) | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  // docs/107 オフライン残: pmtiles キャッシュ解決状態 + offline 自動切替。
  const [tileSource, setTileSource] = useState<'osm' | 'pmtiles' | 'pending'>('pending');
  const online = useOnlineStatus();

  const { state: insightsState } = useEcwInsights(districtId);
  const { state: ideasState } = useEcwIdeas(districtId);
  const insights =
    insightsState.status === 'ready' ? insightsState.items : ([] as InsightMinimal[]);
  const ideas = ideasState.status === 'ready' ? ideasState.items : ([] as IdeaMinimal[]);

  // === old2 EcwHeatmapState 相当の state (M5 default で初期化) ====================
  const initialRadius = heatmapRules?.defaultRadius ?? DEFAULT_RADIUS;
  const initialIntensity = heatmapRules?.defaultIntensity ?? DEFAULT_INTENSITY;
  const initialOpacity = heatmapRules?.defaultOpacity ?? DEFAULT_OPACITY;
  const initialMode: WeightMode = heatmapRules?.defaultWeightMode ?? 'count';
  const weightField = heatmapRules?.weightField;
  const weightAgg = heatmapRules?.weightAgg;
  const weightFormula = heatmapRules?.weightFormula;
  const secondaryWeightField = heatmapRules?.secondaryWeightField;

  const [targetType, setTargetType] = useState<'insight' | 'idea'>('insight');
  const [showMarkers, setShowMarkers] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [radius, setRadius] = useState(initialRadius);
  const [blur, setBlur] = useState(DEFAULT_BLUR);
  const [intensity, setIntensity] = useState(initialIntensity);
  const [opacity, setOpacity] = useState(initialOpacity);
  const [markerSizeBase, setMarkerSizeBase] = useState(DEFAULT_MARKER_SIZE);
  const [weightZero, setWeightZero] = useState(DEFAULT_WEIGHT_ZERO);
  const [weightOne, setWeightOne] = useState(DEFAULT_WEIGHT_ONE);
  const [weightMultipleBase, setWeightMultipleBase] = useState(DEFAULT_WEIGHT_MULTIPLE_BASE);
  const [weightMode, setWeightMode] = useState<WeightMode>(initialMode);
  // old2 L73-74: モバイルは初期折りたたみ。コントロールパネル開閉。
  const [isPanelOpen, setIsPanelOpen] = useState(
    typeof window === 'undefined' ? true : window.innerWidth > 768,
  );

  // heatmapRules 切替時に slider 初期値を追従させる (admin 保存反映 / config 切替)
  useEffect(() => {
    setRadius(initialRadius);
    setIntensity(initialIntensity);
    setOpacity(initialOpacity);
    setWeightMode(initialMode);
  }, [initialRadius, initialIntensity, initialOpacity, initialMode]);

  // 色分け軸の既定: 軸候補が出たら最初の軸を採用 (単色より意味のある初期表示)。一度だけ。
  const colorAxisInitRef = useRef(false);
  useEffect(() => {
    if (!colorAxisInitRef.current && axisFilter.axisOptions.length > 0) {
      colorAxisInitRef.current = true;
      setColorAxisKey(axisFilter.axisOptions[0]!.key);
    }
  }, [axisFilter.axisOptions]);

  // === 軸フィルタ → 日付フィルタ (old2 L111-130 + 軸駆動フィルタ) =================
  // 先に軸駆動フィルタ (axisFilter.filteredPosts)、続いて日付で絞る。
  const axisFilteredPosts = axisFilter.filteredPosts;
  const displayPosts = useMemo(() => {
    if (!startDate && !endDate) return axisFilteredPosts;
    return axisFilteredPosts.filter((p) => {
      const dStr = postDateString(p);
      if (!dStr) return true; // 日付不明は常に表示 (old2 同方針)
      if (startDate && dStr < normalizeDate(startDate)) return false;
      if (endDate && dStr > normalizeDate(endDate)) return false;
      return true;
    });
  }, [axisFilteredPosts, startDate, endDate]);

  // === 関係密度カウント (old2 L132-159) =========================================
  const postCounts = useMemo(
    () => computePostCounts(displayPosts, targetType, insights, ideas),
    [displayPosts, targetType, insights, ideas],
  );

  useEffect(() => {
    selectHandlerRef.current = onSelectPost
      ? (postId: string) => {
          const target = displayPosts.find((p) => p.id === postId);
          if (target) onSelectPost(target);
        }
      : null;
  }, [onSelectPost, displayPosts]);

  // === GeoJSON feature collection (heatmap-weight 用) ===========================
  const featureCollection = useMemo<GeoJSON.FeatureCollection<GeoJSON.Point>>(() => {
    const features: GeoJSON.Feature<GeoJSON.Point>[] = [];
    for (const p of displayPosts) {
      if (!p.location) continue;
      const count = postCounts[p.id] ?? 0;
      const weight = computeWeight(
        p,
        count,
        weightMode,
        weightZero,
        weightOne,
        weightMultipleBase,
        weightField,
        weightAgg,
        weightFormula,
        secondaryWeightField,
      );
      if (weight <= 0) continue;
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [p.location.longitude, p.location.latitude] },
        properties: { id: p.id, weight },
      });
    }
    return { type: 'FeatureCollection', features };
  }, [
    displayPosts,
    postCounts,
    weightMode,
    weightZero,
    weightOne,
    weightMultipleBase,
    weightField,
    weightAgg,
    weightFormula,
    secondaryWeightField,
  ]);
  const postsWithLocation = useMemo(
    () => displayPosts.filter((p) => p.location).length,
    [displayPosts],
  );

  // === 色分けマーカー (count >= 1 の投稿)。色は「色分け軸」(master/postField/M5 由来) で動的に塗る。
  //     旧 Happy×Health 固定象限色は廃し、どのモードでも意味のある軸で着色 (未選択は単色 fallback)。
  const quadrantMarkers = useMemo<OverlayMarker[]>(() => {
    const out: OverlayMarker[] = [];
    for (const p of displayPosts) {
      if (!p.location) continue;
      const count = postCounts[p.id] ?? 0;
      // 関連件数 0 の投稿はマーカー非表示。0 件は heatmap 層のシャドウのみで表現 (old2 L266-270)。
      if (count < 1) continue;
      const axisColor = colorAxisKey ? axisFilter.colorForPost(p, colorAxisKey) : null;
      out.push({
        id: p.id,
        lng: p.location.longitude,
        lat: p.location.latitude,
        color: axisColor ?? DEFAULT_MARKER_COLOR,
        // サイズは関連件数 (密度) で拡縮。ECW 専用の |Happy|*2 ハードコードは廃止。
        size: markerSizeBase + Math.min(count - 1, 4) * 2,
        emphasized: count >= 2,
        title: `${p.title || '(タイトルなし)'}\n関連${targetType === 'insight' ? '気づき' : '処方箋'}: ${count}件`,
        count,
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    displayPosts,
    postCounts,
    markerSizeBase,
    targetType,
    colorAxisKey,
    axisFilter.filterGroupsByKey,
  ]);

  // === pmtiles キャッシュ解決 (docs/107 オフライン残) ===========================
  // pmtilesMeta があり IndexedDB にキャッシュ済なら blob URL を用意して tileSource='pmtiles'。
  // 無ければ 'osm'。これが 'pending' の間は Map を生成しない (機内モードで OSM を取りに行かせない)。
  useEffect(() => {
    let cancelled = false;
    async function resolveTileSource() {
      if (!districtId || !pmtilesMeta) {
        if (!cancelled) setTileSource('osm');
        return;
      }
      try {
        const cached: OfflineMapEntry | null = await lookupValidCache(districtId, pmtilesMeta);
        if (cancelled) return;
        if (cached) {
          blobUrlRef.current = URL.createObjectURL(cached.blob);
          setTileSource('pmtiles');
        } else {
          setTileSource('osm');
        }
      } catch {
        if (!cancelled) setTileSource('osm');
      }
    }
    void resolveTileSource();
    return () => {
      cancelled = true;
    };
  }, [districtId, pmtilesMeta]);

  // unmount で blob URL を revoke。
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  // === Map 初期化 (base style 確定後 / online 変化で作り直し) ====================
  // docs/107 オフライン残: offline (navigator.onLine=false) かつ pmtiles キャッシュありなら
  // OSM ラスターでなく pmtiles (glyphs + 地名 symbol) ベースで立ち上げる。online 復帰で OSM に戻す。
  useEffect(() => {
    if (!container.current) return;
    if (tileSource === 'pending') return;
    const center = centerOverride ?? DEFAULT_CENTER;
    const zoom = zoomOverride ?? DEFAULT_ZOOM;
    const wantOffline = !online && tileSource === 'pmtiles' && !!blobUrlRef.current;
    let baseStyle: maplibregl.StyleSpecification;
    if (wantOffline && blobUrlRef.current) {
      const pmtilesUrl = addPmtilesSource(blobUrlRef.current);
      // schema 対応 (R-35.2): blob は cache 検証で meta.schema と一致済。
      baseStyle = buildPmtilesStyle(pmtilesUrl, pmtilesMeta?.schema ?? 'protomaps');
    } else {
      baseStyle = {
        version: 8,
        sources: {
          osm: {
            type: 'raster',
            tiles: [OSM_TILE_URL],
            tileSize: 256,
            attribution: OSM_ATTRIBUTION,
          },
        },
        layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
      };
    }
    const map = new maplibregl.Map({
      container: container.current,
      style: baseStyle,
      center: [center.lng, center.lat],
      zoom,
    });
    mapRef.current = map;
    setMapGeneration((v) => v + 1);

    map.on('load', () => {
      mapLoadedRef.current = true;
      map.addSource(HEATMAP_SOURCE_ID, { type: 'geojson', data: featureCollection });
      map.addLayer({
        id: HEATMAP_LAYER_ID,
        type: 'heatmap',
        source: HEATMAP_SOURCE_ID,
        maxzoom: 22,
        paint: {
          'heatmap-weight': ['coalesce', ['get', 'weight'], 0],
          'heatmap-intensity': intensity,
          'heatmap-radius': radius,
          'heatmap-opacity': opacity,
          // docs/107 Wave7 / old2 HeatmapView.tsx L191-201 — 8 stop gradient (意味色維持)。
          'heatmap-color': [
            'interpolate',
            ['linear'],
            ['heatmap-density'],
            0,
            'rgba(0, 0, 255, 0)',
            0.15,
            'rgba(65, 105, 225, 0.4)',
            0.3,
            'royalblue',
            0.45,
            'cyan',
            0.6,
            'lime',
            0.75,
            'yellow',
            0.9,
            'orange',
            1,
            'red',
          ],
        },
      });
    });

    return () => {
      quadrantMarkersRef.current.forEach((m) => m.remove());
      quadrantMarkersRef.current = [];
      mapLoadedRef.current = false;
      map.remove();
      mapRef.current = null;
    };
    // docs/107 オフライン残: tileSource 確定 / online 変化で base を作り直す (raster ⇄ pmtiles 切替)。
    // featureCollection / radius 等は生成時の closure 値を使い、以降は別 effect が setData/setPaint で同期。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tileSource, online]);

  // docs/107 Wave2: container 寸法変化 (タブ keep-alive の display:none→block 含む) で map.resize()。
  useEffect(() => {
    const el = container.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => mapRef.current?.resize());
    ro.observe(el);
    const onActivated = () => requestAnimationFrame(() => mapRef.current?.resize());
    window.addEventListener('analysisTabActivated', onActivated);
    return () => {
      ro.disconnect();
      window.removeEventListener('analysisTabActivated', onActivated);
    };
  }, []);

  // posts (featureCollection) 変化で source.setData
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const source = map.getSource(HEATMAP_SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
    if (source && 'setData' in source) {
      source.setData(featureCollection);
    }
  }, [featureCollection, mapGeneration]);

  // slider 値変化で paint property 即時反映 (radius は blur を加算して擬似ぼかし、old2 同方針)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer(HEATMAP_LAYER_ID)) return;
    map.setPaintProperty(HEATMAP_LAYER_ID, 'heatmap-radius', radius);
  }, [radius]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer(HEATMAP_LAYER_ID)) return;
    map.setPaintProperty(HEATMAP_LAYER_ID, 'heatmap-intensity', intensity);
  }, [intensity]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer(HEATMAP_LAYER_ID)) return;
    map.setPaintProperty(HEATMAP_LAYER_ID, 'heatmap-opacity', opacity);
  }, [opacity]);

  // === 象限色マーカーの生成 / 更新 / 破棄 (old2 L254-301) ========================
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    quadrantMarkersRef.current.forEach((m) => m.remove());
    quadrantMarkersRef.current = [];
    if (!showMarkers) return;
    for (const pt of quadrantMarkers) {
      const el = document.createElement('div');
      el.className = 'analysis-heatmap-marker';
      // old2 L284-287: position は MapLibre に委ね、サイズだけ指定。強調は border 太さのみ。
      el.style.width = `${pt.size}px`;
      el.style.height = `${pt.size}px`;
      el.style.borderRadius = '50%';
      el.style.backgroundColor = pt.color;
      el.style.borderColor = pt.color;
      el.style.borderStyle = 'solid';
      el.style.borderWidth = pt.emphasized ? '3px' : '1px';
      el.style.boxSizing = 'border-box';
      el.style.boxShadow = 'none';
      el.style.cursor = 'pointer';
      el.title = pt.title;
      el.setAttribute('aria-label', pt.title);
      // 地図表示と同じく、マーカーを選ぶと投稿詳細を開く (2026-09-07)。
      // マーカーは MapLibre が直接 DOM に挿すため、React ではなく addEventListener で繋ぐ。
      // 押した瞬間の投稿一覧を参照できるよう ref 経由で読む (effect の依存を増やして
      // マーカーを作り直さないため)。
      if (onSelectPost) {
        el.setAttribute('role', 'button');
        el.tabIndex = 0;
        const fire = () => selectHandlerRef.current?.(pt.id);
        el.addEventListener('click', fire);
        el.addEventListener('keydown', (ev) => {
          if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault();
            fire();
          }
        });
      }
      const marker = new maplibregl.Marker({ element: el }).setLngLat([pt.lng, pt.lat]).addTo(map);
      quadrantMarkersRef.current.push(marker);
    }
  }, [quadrantMarkers, showMarkers, onSelectPost, mapGeneration]);

  const emphasizedCount = useMemo(
    () => quadrantMarkers.filter((m) => m.emphasized).length,
    [quadrantMarkers],
  );

  // score option ラベル (M5 weightField/weightFormula 由来、未指定は default)。
  const productSecondaryLabel =
    secondaryWeightField && secondaryWeightField.trim() !== ''
      ? secondaryWeightField
      : 'HealthScore';
  const scoreLabel = weightFormula
    ? weightFormula
    : weightField
      ? weightAgg === 'product' || weightAgg === 'abs-product'
        ? `${weightField} × ${productSecondaryLabel}`
        : weightField
      : '|HAPPY × HEALTHY|';

  const targetWord = targetType === 'insight' ? '気づき' : '処方箋';

  return (
    <div className="relative h-full w-full overflow-hidden">
      {/* map container (背面) */}
      <div ref={container} className="absolute inset-0" />

      {/* docs/107 Wave7 / old2 L545-586 — 折りたたみ右上コントロールパネル (v2 semantic token 化) */}
      <div
        className={`absolute top-3 right-3 z-20 rounded-lg border border-slate-200 bg-white/95 shadow-lg backdrop-blur ${
          isPanelOpen ? 'w-[300px] max-w-[calc(100%-1.5rem)]' : 'w-10'
        } max-h-[calc(100%-1.5rem)] overflow-y-auto`}
      >
        {!isPanelOpen ? (
          <button
            type="button"
            onClick={() => setIsPanelOpen(true)}
            className="flex h-10 w-10 items-center justify-center text-lg text-slate-600 hover:text-slate-900"
            aria-label="ヒートマップ設定を開く"
            title="メニューを開く"
          >
            ☰
          </button>
        ) : (
          <div className="p-3">
            <button
              type="button"
              onClick={() => setIsPanelOpen(false)}
              className="mb-3 flex w-full items-center justify-between border-b border-slate-200 pb-2"
              aria-label="ヒートマップ設定を閉じる"
              title="メニューを閉じる"
            >
              <h2 className="text-sm font-bold text-slate-800">ヒートマップ設定</h2>
              <span className="text-lg leading-none text-slate-400">✕</span>
            </button>

            <div className="flex flex-col gap-4">
              {/* 表示対象の切り替え (targetType) */}
              <fieldset>
                <legend className="mb-1.5 text-xs font-bold text-slate-700">
                  表示対象の切り替え
                </legend>
                <label className="mb-1.5 flex cursor-pointer flex-col text-xs text-slate-700">
                  <span className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      name="heatmap-targetType"
                      value="insight"
                      checked={targetType === 'insight'}
                      onChange={() => setTargetType('insight')}
                      aria-label="気づきが生まれた投稿"
                    />
                    ① 気づきが生まれた投稿
                  </span>
                  <span className="ml-5 mt-0.5 text-[10px] text-slate-400">
                    1つの投稿から2つ以上の気づきが生まれたら強調表示
                  </span>
                </label>
                <label className="flex cursor-pointer flex-col text-xs text-slate-700">
                  <span className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      name="heatmap-targetType"
                      value="idea"
                      checked={targetType === 'idea'}
                      onChange={() => setTargetType('idea')}
                      aria-label="処方箋が生まれた投稿"
                    />
                    ② 処方箋が生まれた投稿
                  </span>
                  <span className="ml-5 mt-0.5 text-[10px] text-slate-400">
                    1つの投稿から2つ以上の処方箋が生まれたら強調表示
                  </span>
                </label>
              </fieldset>

              {/* 日付フィルター */}
              <div>
                <div className="mb-1.5 text-xs font-bold text-slate-700">日付フィルター</div>
                <div className="flex flex-col gap-1.5">
                  <label className="flex items-center gap-2 text-xs text-slate-600">
                    <span className="w-8">開始</span>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      aria-label="日付フィルター開始"
                      className="flex-1 rounded border border-slate-300 px-2 py-1 text-xs"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-600">
                    <span className="w-8">終了</span>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      aria-label="日付フィルター終了"
                      className="flex-1 rounded border border-slate-300 px-2 py-1 text-xs"
                    />
                  </label>
                  {(startDate || endDate) && (
                    <button
                      type="button"
                      onClick={() => {
                        setStartDate('');
                        setEndDate('');
                      }}
                      className="mt-1 rounded border border-slate-300 bg-slate-50 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
                    >
                      日付フィルターをクリア
                    </button>
                  )}
                </div>
              </div>

              {/* 色分け軸 (マーカー色) + 軸駆動フィルタ。
                  master / dataSettings.postFields / M5 由来でどのモードでも動的 (マトリクスと同じ)。 */}
              <div>
                <div className="mb-1.5 text-xs font-bold text-slate-700">色分け・フィルタ</div>
                <label className="mb-2 flex items-center gap-2 text-xs text-slate-600">
                  <span className="shrink-0">色分け軸</span>
                  <select
                    value={colorAxisKey}
                    onChange={(e) => setColorAxisKey(e.target.value)}
                    aria-label="マーカーの色分け軸"
                    className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-1 text-xs"
                  >
                    <option value="">単色</option>
                    {axisFilter.axisOptions.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <AxisFilterPanel
                  filter={axisFilter}
                  open={showFilters}
                  onToggleOpen={() => setShowFilters((v) => !v)}
                  compact
                />
              </div>

              {/* マーカー表示 */}
              <div>
                <div className="mb-1.5 text-xs font-bold text-slate-700">マーカー表示</div>
                <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    checked={showMarkers}
                    onChange={(e) => setShowMarkers(e.target.checked)}
                    aria-label="投稿マーカー表示"
                  />
                  投稿マーカー（●）を表示する
                </label>
                {showMarkers && (
                  <div className="ml-5 mt-2">
                    <div className="mb-1 flex justify-between text-[10px] text-slate-500">
                      <span>マーカーの大きさ</span>
                      <span>{markerSizeBase}px</span>
                    </div>
                    <input
                      type="range"
                      min={4}
                      max={36}
                      step={1}
                      value={markerSizeBase}
                      onChange={(e) => setMarkerSizeBase(Number(e.target.value))}
                      aria-label="マーカーの大きさ"
                      className="w-full"
                    />
                  </div>
                )}
              </div>

              {/* 重みモード (件数密度 ↔ スコア) — v2 拡張 */}
              <div>
                <div className="mb-1.5 text-xs font-bold text-slate-700">重みの計算方法</div>
                <select
                  value={weightMode}
                  onChange={(e) => setWeightMode(e.target.value as WeightMode)}
                  aria-label="ヒートマップ重み計算モード"
                  className="min-h-touch w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs"
                >
                  <option value="count">関係密度（{targetWord}件数）</option>
                  <option value="score">{scoreLabel}</option>
                </select>
              </div>

              {/* レベル補正（データ比重） — 関係密度 3 段スライダー (count モード時のみ) */}
              {weightMode === 'count' && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="mb-2.5 text-xs font-bold text-slate-700">
                    📊 レベル補正（データ比重）
                  </div>
                  <div className="flex flex-col gap-3">
                    <div>
                      <div className="mb-1 flex justify-between text-[10px] text-slate-500">
                        <span>シャドウ（0件の重み）</span>
                        <span>{weightZero.toFixed(2)}</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.01}
                        value={weightZero}
                        onChange={(e) => setWeightZero(Number(e.target.value))}
                        aria-label="シャドウ（0件の重み）"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <div className="mb-1 flex justify-between text-[10px] text-slate-500">
                        <span>中間調（1件の重み）</span>
                        <span>{weightOne.toFixed(2)}</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={2}
                        step={0.05}
                        value={weightOne}
                        onChange={(e) => setWeightOne(Number(e.target.value))}
                        aria-label="中間調（1件の重み）"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <div className="mb-1 flex justify-between text-[10px] text-slate-500">
                        <span>ハイライト（2件以上の重み）</span>
                        <span>{weightMultipleBase.toFixed(1)}</span>
                      </div>
                      <input
                        type="range"
                        min={1}
                        max={10}
                        step={0.5}
                        value={weightMultipleBase}
                        onChange={(e) => setWeightMultipleBase(Number(e.target.value))}
                        aria-label="ハイライト（2件以上の重み）"
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* 描画効果パラメータ — radius / blur / intensity / opacity */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="mb-2.5 text-xs font-bold text-slate-700">🎨 描画効果パラメータ</div>
                <div className="flex flex-col gap-3">
                  <div>
                    <div className="mb-1 flex justify-between text-[10px] text-slate-500">
                      <span>広がり（Radius）</span>
                      <span>{radius}px</span>
                    </div>
                    <input
                      type="range"
                      min={5}
                      max={100}
                      step={1}
                      value={radius}
                      onChange={(e) => setRadius(Number(e.target.value))}
                      aria-label="ヒートマップ半径"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <div className="mb-1 flex justify-between text-[10px] text-slate-500">
                      <span>ぼかし（Blur）</span>
                      <span>{blur}px</span>
                    </div>
                    <input
                      type="range"
                      min={5}
                      max={100}
                      step={1}
                      value={blur}
                      onChange={(e) => setBlur(Number(e.target.value))}
                      aria-label="ヒートマップぼかし"
                      className="w-full"
                    />
                    <div className="mt-1 text-[10px] text-slate-400">
                      ※ MapLibre では radius に内包されます
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 flex justify-between text-[10px] text-slate-500">
                      <span>上限しきい値（Max: 色づきの速さ）</span>
                      <span>{intensity.toFixed(1)}</span>
                    </div>
                    <input
                      type="range"
                      min={0.1}
                      max={10}
                      step={0.1}
                      value={intensity}
                      onChange={(e) => setIntensity(Number(e.target.value))}
                      aria-label="ヒートマップ強度"
                      className="w-full"
                    />
                    <div className="mt-1 text-[10px] text-slate-400">
                      ※ 大きいほどすぐ赤くなり、小さいほど密集が必要になります
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 flex justify-between text-[10px] text-slate-500">
                      <span>ヒートマップの不透明度</span>
                      <span>{Math.round(opacity * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min={0.1}
                      max={1}
                      step={0.05}
                      value={opacity}
                      onChange={(e) => setOpacity(Number(e.target.value))}
                      aria-label="ヒートマップ透明度"
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 凡例 / 件数 badge (件数 + 色分け軸の bucket 凡例を左下に常時表示)。 */}
      <div className="pointer-events-none absolute bottom-3 left-3 z-10 max-w-[60vw] rounded border border-slate-200 bg-white/90 px-2 py-1.5 text-[11px] text-slate-700 shadow backdrop-blur">
        <div className="mb-1 font-bold text-slate-600">
          位置情報あり {postsWithLocation} 件 / 全 {displayPosts.length} 件
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-full border border-white"
            style={{ backgroundColor: DEFAULT_MARKER_COLOR }}
            aria-hidden
          />
          <span>
            関連{targetWord}あり {quadrantMarkers.length} 件
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-full border-[2px] border-slate-700"
            style={{ backgroundColor: DEFAULT_MARKER_COLOR }}
            aria-hidden
          />
          <span>2件以上で強調 {emphasizedCount} 件</span>
        </div>
        {/* 色分け軸の bucket 凡例 (動的)。軸選択時のみ。 */}
        {colorAxisKey &&
          (() => {
            const groups = axisFilter.groupsForAxis(colorAxisKey).slice(0, 8);
            if (groups.length === 0) return null;
            return (
              <div className="mt-1 border-t border-slate-200 pt-1">
                <div className="mb-0.5 text-[10px] font-semibold text-slate-500">
                  色:{' '}
                  {axisFilter.axisOptions.find((o) => o.key === colorAxisKey)?.label ??
                    colorAxisKey}
                </div>
                <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                  {groups.map((g) => (
                    <span key={g.code} className="flex items-center gap-1">
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{ backgroundColor: g.color }}
                        aria-hidden
                      />
                      <span className="text-[10px]">{g.label}</span>
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}
      </div>
    </div>
  );
}
